import { shallowEqual } from 'react-redux';

import { Flex, Image, Spacer, DetailedIconVirtualTour } from '@zillow/constellation';
import { Button, Heading, Text } from '@zillow/constellation-10';

import { useSelector } from '../../../../../selectors/useSelector';

import AppDownload from '../../../../../images/AppDownload.svg';
import TourAddress from '../../../../../images/TourAddress.svg';

import { StyledCard, StyledCardText, StyledTitle } from './StyledComponents';

type ActionCardProps = {
  detailedIcon?: JSX.Element;
  iconSrc?: string;
  iconAlt?: string;
  title: string;
  description: string;
  cta: string;
  onClick: () => void;
};

const ActionCard = ({ detailedIcon, iconSrc, iconAlt, title, description, cta, onClick }: ActionCardProps) => (
  <StyledCard cardType="secondary">
    {iconSrc ? <Image src={iconSrc} alt={iconAlt} /> : null}
    {detailedIcon ? detailedIcon : null}
    <StyledCardText>
      <Text textStyle="body-sm-bold" css={{ color: 'blue.400' }}>
        {title}
      </Text>
      <Text>{description}</Text>
    </StyledCardText>
    <Button
      tone="brand"
      emphasis="outlined"
      onClick={onClick}
      fluid
      css={{
        flexShrink: 0,
        md: {
          width: 'auto',
        },
      }}
    >
      {cta}
    </Button>
  </StyledCard>
);

const EmptyTours = () => {
  const { isMultifamily = false } = useSelector(
    (state) => ({
      isMultifamily: state?.properties?.currentProperty?.isMultifamily,
    }),
    shallowEqual,
  );

  const handleDownloadAppClick = () => {
    window.open('https://www.zillow.com/marketing/3d-home/', '_blank');
  };

  const handleVirtualTourClick = () => {
    window.open('https://showingtimeplus.com/solutions/zillow-media-experts/', '_blank');
  };

  return (
    <Flex display="flex" flexDirection="column" alignItems="center">
      <Spacer paddingY={{ default: 'xs', md: 'sm' }}>
        <StyledTitle>
          <Heading level={3}>Show off your property with a 3D tour</Heading>
        </StyledTitle>
      </Spacer>

      {window.innerHeight > 800 && (
        <Spacer paddingY={{ default: 'xs', md: 'sm' }}>
          <Image src={TourAddress} alt="Tour address" />
        </Spacer>
      )}

      <Spacer paddingY={{ default: 'xs', md: 'sm' }}>
        <ActionCard
          iconSrc={AppDownload}
          iconAlt="App Download"
          title="Create a tour"
          description="Create a tour for free with the Zillow 3D Home app"
          cta="Download app"
          onClick={handleDownloadAppClick}
        />
      </Spacer>

      {isMultifamily && (
        <Spacer paddingY={{ default: 'xs', md: 'sm' }}>
          <ActionCard
            detailedIcon={<DetailedIconVirtualTour title="Virtual Tour" />}
            title="Hire a photographer"
            description="Impress renters with professionally shot 3D tours"
            cta="Explore options"
            onClick={handleVirtualTourClick}
          />
        </Spacer>
      )}
    </Flex>
  );
};

export default EmptyTours;
